// apps/web/src/pages/admin/reservations/ReservationEditPage.tsx — Phase 5 plan 05-04 (ADM-03).
//
// Admin-only edit surface. Pre-fills the form from Query.reservation;
// on submit, fires `updateReservation` + `useInvalidateAdminCaches`. RBAC:
// service role is redirected to the Reservations list (D-10).

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ClientError } from "graphql-request";
import { useTranslation } from "react-i18next";
import { gqlRequest } from "../../../lib/api";
import { useInvalidateAdminCaches } from "../../../lib/admin-cache";
import { useAuth } from "../../../lib/auth";
import { RESERVATION_QUERY } from "./reservations-operations";
import {
  ALLOWED_SOURCE,
  ALLOWED_STATUS,
  type ReservationSourceFilter,
  type ReservationStatusFilter,
} from "./ReservationFilters";

const UPDATE_RESERVATION_MUTATION = /* GraphQL */ `
  mutation UpdateReservationAdmin($id: ID!, $input: UpdateReservationInput!) {
    updateReservation(id: $id, input: $input) {
      id
      reservationNumber
      status
    }
  }
`;

export interface ReturnFilter {
  date?: string;
  search?: string;
  status: ReservationStatusFilter[];
  source: ReservationSourceFilter[];
}

export function decodeReturnFilter(
  searchParams: URLSearchParams,
): ReturnFilter {
  const date = searchParams.get("from_date") ?? undefined;
  const search = searchParams.get("from_search") ?? undefined;
  const statusRaw = searchParams.get("from_status");
  const sourceRaw = searchParams.get("from_source");
  const status = (statusRaw ? statusRaw.split(",") : [])
    .map((item) => item.trim())
    .filter((item): item is ReservationStatusFilter =>
      (ALLOWED_STATUS as readonly string[]).includes(item),
    );
  const source = (sourceRaw ? sourceRaw.split(",") : [])
    .map((item) => item.trim())
    .filter((item): item is ReservationSourceFilter =>
      (ALLOWED_SOURCE as readonly string[]).includes(item),
    );

  return {
    ...(date === undefined ? {} : { date }),
    ...(search === undefined ? {} : { search }),
    status,
    source,
  };
}

export function buildReturnPath(filter: ReturnFilter): string {
  const params = new URLSearchParams();
  if (filter.date) params.set("date", filter.date);
  if (filter.search) params.set("search", filter.search);
  if (filter.status.length) params.set("status", filter.status.join(","));
  if (filter.source.length) params.set("source", filter.source.join(","));
  const query = params.toString();

  return query ? `/admin/reservierungen?${query}` : "/admin/reservierungen";
}

export function ReservationEditPage() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encodedReturnFilter = searchParams.toString();
  const returnFilter = useMemo(
    () => decodeReturnFilter(new URLSearchParams(encodedReturnFilter)),
    [encodedReturnFilter],
  );
  const returnPath = useMemo(
    () => buildReturnPath(returnFilter),
    [returnFilter],
  );
  const invalidate = useInvalidateAdminCaches();
  const { state } = useAuth();
  const user = state.user;

  const id = params.id ?? "";

  useEffect(() => {
    if (user?.role === "SERVICE") {
      navigate(returnPath, { replace: true });
    }
  }, [user, navigate, returnPath]);

  const reservation = useQuery({
    queryKey: ["reservation", id],
    queryFn: async () => {
      const res = await gqlRequest<{
        reservation: {
          id: string;
          reservationNumber: string;
          status: string;
          source: string;
          startsAt: string;
          durationMinutes: number;
          partySize: number;
          guestName: string;
          guestEmail: string | null;
          guestPhone: string | null;
          internalNote: string | null;
          slotDefinitionId: string;
          serviceDate: string;
        } | null;
      }>(RESERVATION_QUERY, { id });
      return res.reservation;
    },
    enabled: !!id,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (reservation.data) {
      setName(reservation.data.guestName);
      setEmail(reservation.data.guestEmail ?? "");
      setPhone(reservation.data.guestPhone ?? "");
      setInternalNote(reservation.data.internalNote ?? "");
      setPartySize(reservation.data.partySize);
    }
  }, [reservation.data]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const res = await gqlRequest<{
        updateReservation: { id: string; reservationNumber: string };
      }>(UPDATE_RESERVATION_MUTATION, {
        id,
        input: {
          name,
          email: email || null,
          phone: phone || null,
          internalNote: internalNote || null,
          partySize,
        },
      });
      return res.updateReservation;
    },
    onSuccess: () => {
      invalidate();
      navigate(returnPath);
    },
    onError: (err) => {
      setError(err instanceof ClientError ? err.message : "Error");
    },
  });

  if (state.hydrating || !user) {
    // Cold-start hydration window: we don't know who the user is yet,
    // so RBAC decisions are suspended. Render a neutral loading block
    // instead of the `edit-not-authorized` empty state — RBAC failures
    // during hydration are a UX bug, not a security failure (RequireAuth
    // already gates accessToken acquisition before children mount).
    return <p className="p-4">…</p>;
  }
  if (user.role === "SERVICE") {
    return (
      <p className="p-4" data-testid="edit-not-authorized">
        {t("admin.reservationEdit.notAuthorized")}
      </p>
    );
  }

  if (reservation.isLoading) {
    return <p className="p-4">…</p>;
  }

  if (reservation.isError || !reservation.data) {
    return (
      <p className="p-4 text-error" data-testid="edit-not-found">
        {t("admin.reservations.errors.notFound")}
      </p>
    );
  }

  return (
    <div className="px-4 py-6 max-w-2xl" data-testid="reservation-edit-page">
      <h1 className="font-heading text-2xl mb-4">
        {t("admin.reservationEdit.pageTitle")}
      </h1>
      <p className="text-sm text-text/60 mb-4">
        {reservation.data.reservationNumber}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          updateMutation.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.reservationEdit.name")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="field"
            data-testid="edit-name"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.reservationEdit.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field"
            data-testid="edit-email"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">{t("admin.reservationEdit.phone")}</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="field"
            data-testid="edit-phone"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.reservationEdit.partySize")}
          </span>
          <input
            type="number"
            min={1}
            max={20}
            value={partySize}
            onChange={(e) => setPartySize(Number(e.target.value))}
            className="field"
            data-testid="edit-party-size"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm">
            {t("admin.reservationEdit.internalNote")}
          </span>
          <textarea
            maxLength={2000}
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            className="field"
            data-testid="edit-internal-note"
          />
        </label>
        {error && (
          <p
            role="alert"
            className="text-sm text-error"
            data-testid="edit-error"
          >
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn btn-primary"
            data-testid="edit-save"
          >
            {t("admin.reservationEdit.save")}
          </button>
          <button
            type="button"
            onClick={() => navigate(returnPath)}
            className="btn btn-outline"
            data-testid="edit-cancel"
          >
            {t("admin.reservationEdit.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
